import FVector from '@data/geometry/FVector';
import { OctTree } from '@data/OctTree';
import { CONTEXT_KEYS, StageContext } from '@data/Simulations/pipeline/Context';
import { FilterStage, StageParameters } from '@data/Simulations/pipeline/Stage';
import { UnobstructedFaces, UnobstructedMode } from '@visualizer/types';
import Grid from 'Geodata/grid';
import { SensorData, SensorPoint } from 'Simulations/SensorPoint';

export type UnobstructedFilterStageParameters = StageParameters & {
    unobstructedMode: UnobstructedMode;
    isValue: boolean;
};

class UnobstructedFilterStage extends FilterStage<UnobstructedFilterStageParameters> {
    private sensorDataMap = new Map<number, SensorData>();
    private octTree?: OctTree;
    private layerMinValue = 0;

    constructor(params: UnobstructedFilterStageParameters) {
        super(params);
    }

    protected override processAll(context: StageContext): StageContext {
        this.sensorDataMap = new Map(context.sensors.map((s) => [s.getSensorPoint().getID(), s]));
        this.octTree = context.octTree;
        this.layerMinValue = context.absoluteMinMax[0];

        // Create a map if not present:
        const newData = new Map(context.data);
        newData.set(CONTEXT_KEYS.UNOBSTRUCTED_FREEFACES, new Map<number, Set<string>>());

        const updatedContext: StageContext = {
            ...context,
            data: newData,
        };

        const sensorDatas = updatedContext.sensors;
        const toRemove = new Set<SensorData>();

        for (const sd of sensorDatas) {
            const active = this.filter(sd);
            if (!active) toRemove.add(sd);
        }

        const filtered = sensorDatas.filter((s) => !toRemove.has(s));

        return { ...updatedContext, sensors: filtered };
    }

    protected override filter(sensorData: SensorData): boolean {
        const vResult = sensorData.getVectorialResult().normalize();
        if (vResult.mag() === 0) return false;

        const mode = this.getParams().unobstructedMode;

        let isActive = false;
        let freeFaces: Set<number> = new Set();

        switch (mode) {
            case UnobstructedMode.Center:
                ({ isActive, freeFaces } = this.fromVoxelCenter(sensorData, vResult));
                break;

            case UnobstructedMode.Corners:
                ({ isActive, freeFaces } = this.fromVoxelCorners(sensorData, vResult));
                break;

            default:
                console.warn('UnobstructedStage: unknown mode', mode);
                return false;
        }

        // Update values:
        if (this.getParams().isValue) {
            this.updateValue(sensorData, freeFaces);
        }

        return isActive;
    }

    // =====================================
    // FILTERING
    // =====================================

    private fromVoxelCenter(sensorData: SensorData, vResult: FVector): { isActive: boolean; freeFaces: Set<number> } {
        const position = sensorData.getPosition();
        const ray = { start: position, direction: vResult };

        const isObstructed = this.checkObstruction(ray, sensorData);
        const isFree = !isObstructed;

        // Still use processCorners to compute freeFaces for value:
        const { freeFaces } = this.processCorners(sensorData, vResult, (ray, sd) => this.checkObstruction(ray, sd));

        return { isActive: isFree, freeFaces };
    }

    private fromVoxelCorners(sensorData: SensorData, vResult: FVector): { isActive: boolean; freeFaces: Set<number> } {
        return this.processCorners(sensorData, vResult, (ray, sd) => this.checkObstruction(ray, sd));
    }

    private processCorners(
        sensorData: SensorData,
        vResult: FVector,
        callback: (ray: { start: any; direction: FVector }, sensorData: SensorData) => any
    ): { isActive: boolean; freeFaces: Set<number> } {
        // === STEP 1: Set as obstructed if all neighbours are active ===
        const neighbours = this.getNeighboursByIndex(sensorData);
        const activeNeighbours = neighbours.filter((sd) => sd && sd.getScalarValue() !== 0);
        if (activeNeighbours.length === 6) return { isActive: false, freeFaces: new Set() };

        // === STEP 2: Determine relevant 3 directions from vResult ===
        const directionOffsets: { x: number; y: number; z: number }[] = [];
        if (vResult.x !== 0) directionOffsets.push({ x: Math.sign(vResult.x), y: 0, z: 0 });
        if (vResult.y !== 0) directionOffsets.push({ x: 0, y: Math.sign(vResult.y), z: 0 });
        if (vResult.z !== 0) directionOffsets.push({ x: 0, y: 0, z: Math.sign(vResult.z) });

        // === STEP 3: Get 3 neighbours on main direction, and collect their face vertices ===
        const point = sensorData.getSensorPoint();
        const pointCenter = point.getCenter();
        const gridRes = Grid.gridRes;
        let sdIsFree = false;

        const faceVerticesMap = new Map<number, { x: number; y: number; z: number }[]>(); // key = 'x', 'y', or 'z'

        for (const offset of directionOffsets) {
            const neighborPos = {
                x: pointCenter.x + offset.x * gridRes,
                y: pointCenter.y + offset.y * gridRes,
                z: pointCenter.z + offset.z * gridRes,
            };

            const neighborSD = activeNeighbours.find(
                (p) =>
                    p.getSensorPoint().getCenter().x === neighborPos.x &&
                    p.getSensorPoint().getCenter().y === neighborPos.y &&
                    p.getSensorPoint().getCenter().z === neighborPos.z
            );

            // Save Face Vertices only if the neighbour doesn't exist, or has a value of 0 (black point):
            let faceVertices: { x: number; y: number; z: number }[] = [];
            if (!neighborSD || neighborSD.getScalarValue() === 0) {
                sdIsFree = true;
                const h = gridRes / 2;

                if (offset.x !== 0) {
                    const x = pointCenter.x + offset.x * h;
                    faceVertices = [
                        { x, y: pointCenter.y - h, z: pointCenter.z - h },
                        { x, y: pointCenter.y + h, z: pointCenter.z - h },
                        { x, y: pointCenter.y - h, z: pointCenter.z + h },
                        { x, y: pointCenter.y + h, z: pointCenter.z + h },
                    ];
                } else if (offset.y !== 0) {
                    const y = pointCenter.y + offset.y * h;
                    faceVertices = [
                        { x: pointCenter.x - h, y, z: pointCenter.z - h },
                        { x: pointCenter.x + h, y, z: pointCenter.z - h },
                        { x: pointCenter.x - h, y, z: pointCenter.z + h },
                        { x: pointCenter.x + h, y, z: pointCenter.z + h },
                    ];
                } else if (offset.z !== 0) {
                    const z = pointCenter.z + offset.z * h;
                    faceVertices = [
                        { x: pointCenter.x - h, y: pointCenter.y - h, z },
                        { x: pointCenter.x + h, y: pointCenter.y - h, z },
                        { x: pointCenter.x - h, y: pointCenter.y + h, z },
                        { x: pointCenter.x + h, y: pointCenter.y + h, z },
                    ];
                }

                const axis = offset.x !== 0 ? UnobstructedFaces.X : offset.y !== 0 ? UnobstructedFaces.Y : UnobstructedFaces.Z;
                if (!faceVerticesMap.has(axis)) faceVerticesMap.set(axis, []);
                faceVerticesMap.get(axis)!.push(...faceVertices);
            }
        }

        // === STEP 4: Cast rays only if sdIsFree ===
        const freeFaces = new Set<number>();
        let centerRayFree = false;
        let anyVertexFree = false;
        if (sdIsFree) {
            centerRayFree = !callback({ start: pointCenter, direction: vResult }, sensorData);
            for (const [axis, vertices] of faceVerticesMap.entries()) {
                let allUnobstructed = true;

                for (const vertex of vertices) {
                    const ray = {
                        start: {
                            x: pointCenter.x + (vertex.x - pointCenter.x),
                            y: pointCenter.y + (vertex.y - pointCenter.y),
                            z: pointCenter.z + (vertex.z - pointCenter.z),
                        },
                        direction: vResult,
                    };

                    const isObstructed = callback(ray, sensorData);
                    if (isObstructed) {
                        allUnobstructed = false;
                        continue;
                    }
                    anyVertexFree = true;
                }

                if (allUnobstructed) {
                    freeFaces.add(axis);
                }
            }
        }

        return { isActive: anyVertexFree || centerRayFree, freeFaces };
    }

    /** Same as old `checkObstruction` but using this.sensors from processAll */
    private checkObstruction(ray: { start: any; direction: FVector }, sensorData: SensorData): boolean {
        const hit = this.octTree?.searchFirstItemByRay(ray, sensorData.getSensorPoint(), (sensorPoint: SensorPoint) => {
            const sd = this.sensorDataMap.get(sensorPoint.getID());
            return !!(sd && sd.getScalarValue() !== 0);
        });

        return !!hit;
    }

    /** Reimplementation of old neighbour mapping using sensorPoint.ID as index */
    private getNeighboursByIndex(sensorData: SensorData): SensorData[] {
        const neighbours = sensorData.getSensorPoint().neighbors6; // SensorPoint[]
        return neighbours.map((sp) => this.sensorDataMap.get(sp.ID)).filter((sd): sd is SensorData => !!sd);
    }

    // =====================================
    // TRANSFORMATION
    // =====================================

    private updateValue(input: SensorData, freeFaces: Set<number> | undefined): SensorData {
        const params = this.getParams();

        if (!params.isValue) return input;

        const direction = input.getVectorialResult();

        // No free faces -> use layer minimum:
        if (!freeFaces || freeFaces.size === 0) {
            input.setScalarValue(this.layerMinValue);
            return input;
        }

        // Compute projection value:
        const directionMagnitude = direction.mag();
        if (directionMagnitude === 0) return input;

        const dir = direction.normalize();
        const visibleFaces = [UnobstructedFaces.X, UnobstructedFaces.Y, UnobstructedFaces.Z].filter((axis) => freeFaces.has(axis));
        const scale = this.computeFaceProjectionScale(visibleFaces, dir);

        // Update only the scalar value; the sensor direction must remain unchanged.
        input.setScalarValue(input.getScalarValue() * scale);

        return input;
    }

    private computeFaceProjectionScale(freeFaces: number[], vResult: FVector): number {
        const vMag = vResult.mag();
        if (vMag === 0) return 0;

        const vNorm = FVector.div(vResult, vMag); // normalize
        const faceNormals: Record<number, FVector> = {
            [UnobstructedFaces.X]: new FVector(Math.sign(vResult.x), 0, 0),
            [UnobstructedFaces.Y]: new FVector(0, Math.sign(vResult.y), 0),
            [UnobstructedFaces.Z]: new FVector(0, 0, Math.sign(vResult.z)),
        };

        let maxContribution = 0;
        for (const face of freeFaces) {
            const normal = faceNormals[face];
            const dot = FVector.dot(vNorm, normal);
            if (dot > maxContribution) {
                maxContribution = dot;
            }
        }

        const maxPossibleContribution = Math.abs(vNorm.x) + Math.abs(vNorm.y) + Math.abs(vNorm.z);

        return maxContribution / maxPossibleContribution;
    }
}

export default UnobstructedFilterStage;
